/**
 * FlowCoachPanel - Right-side AI coach panel for Flow mode
 * Features guided mode toggle and entity creation assistance
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Send,
  Loader2,
  AlertCircle,
  Sparkles,
  Layout,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useWorkspaceCoach, type FlowContext, type QuickAction, parseEntityMentions } from '@/hooks/useWorkspaceCoach'
import { useAIStore } from '@/stores/aiStore'
import { EntitySuggestionCard } from './EntitySuggestionCard'
import { PhaseProgressSection } from './PhaseProgressSection'
import { QuickActionsBar } from './QuickActionsBar'
import { EntityMentionChip } from './EntityMentionChip'
import type { PhaseEntityCard } from '@/hooks/useFlowWorkflow'
import type { JourneyPhaseWithMode } from '@ispot/shared'

interface FlowCoachPanelProps {
  phase: JourneyPhaseWithMode | null
  entityCards: PhaseEntityCard[]
  onCreateEntity?: (type: string, label: string, description: string) => Promise<void>
  className?: string
}

export function FlowCoachPanel({
  phase,
  entityCards,
  onCreateEntity,
  className,
}: FlowCoachPanelProps) {
  const { apiKey } = useAIStore()
  const {
    messages,
    isLoading,
    isStreaming,
    error,
    assistantMode,
    setAssistantMode,
    sendMessage,
    clearMessages,
    isConfigured,
  } = useWorkspaceCoach()

  const [inputValue, setInputValue] = useState('')
  const [isCreatingEntity, setIsCreatingEntity] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const greetingSentRef = useRef(false) // Prevent duplicate sends within same render cycle

  // Build flow context for the assistant
  const buildFlowContext = useCallback((): FlowContext | undefined => {
    if (!phase) return undefined

    const requiredCards = entityCards.filter(c => c.isRequired)
    const filledCards = requiredCards.filter(c => c.currentCount >= c.minCount)
    const missingCards = requiredCards.filter(c => c.currentCount < c.minCount)

    return {
      phaseName: phase.name,
      phaseObjective: phase.objective,
      requiredEntities: requiredCards.map(c => c.className).join(', ') || 'None',
      filledEntities: filledCards.map(c => `${c.className} (${c.currentCount})`).join(', ') || 'None',
      missingEntities: missingCards.map(c => c.className).join(', ') || 'None',
    }
  }, [phase, entityCards])

  // Auto-greet when switching to guided mode (with localStorage persistence)
  useEffect(() => {
    if (assistantMode === 'guided' && isConfigured && phase) {
      // Create a unique key for this phase greeting
      const greetingKey = `flow-coach-greeted-${phase.id}`
      const lastGreeting = localStorage.getItem(greetingKey)
      const now = Date.now()

      // Only greet if we haven't greeted in the last 60 seconds
      // This prevents duplicate greetings on quick tab switches
      if (!lastGreeting || now - parseInt(lastGreeting, 10) > 60000) {
        // Additional guard: prevent multiple sends in same render cycle
        if (!greetingSentRef.current) {
          greetingSentRef.current = true
          localStorage.setItem(greetingKey, now.toString())

          const greeting = `I'm starting ${phase.name}. Help me understand what I need to do.`
          sendMessage(greeting, {
            mode: 'flow',
            phaseId: phase.id,
            phaseName: phase.name,
            journeyMode: 'structured',
            flowContext: buildFlowContext(),
          })

          // Reset guard after a short delay
          setTimeout(() => {
            greetingSentRef.current = false
          }, 1000)
        }
      }
    }
    // Only re-run when mode, phase, or config changes - not when buildFlowContext changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantMode, isConfigured, phase?.id, sendMessage])

  // Clear greeting cache when switching to standard mode
  useEffect(() => {
    if (assistantMode === 'standard' && phase) {
      const greetingKey = `flow-coach-greeted-${phase.id}`
      localStorage.removeItem(greetingKey)
      greetingSentRef.current = false
    }
  }, [assistantMode, phase?.id])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle send
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isStreaming) return
    const content = inputValue.trim()
    setInputValue('')
    await sendMessage(content, {
      mode: 'flow',
      phaseId: phase?.id,
      phaseName: phase?.name,
      journeyMode: 'structured',
      flowContext: buildFlowContext(),
    })
  }, [inputValue, isLoading, isStreaming, sendMessage, phase, buildFlowContext])

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Toggle guided mode
  const handleToggleGuided = useCallback(() => {
    const newMode = assistantMode === 'guided' ? 'standard' : 'guided'
    setAssistantMode(newMode)
    clearMessages()
    greetingSentRef.current = false
  }, [assistantMode, setAssistantMode, clearMessages])

  // Handle entity creation from suggestion
  const handleAcceptSuggestion = async (type: string, label: string, description: string) => {
    if (!onCreateEntity) return

    setIsCreatingEntity(true)
    try {
      await onCreateEntity(type, label, description)
      // Send confirmation message to assistant
      await sendMessage(`I created the ${type}: "${label}"`, {
        mode: 'flow',
        phaseId: phase?.id,
        phaseName: phase?.name,
        journeyMode: 'structured',
        flowContext: buildFlowContext(),
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create entity:', error)
    } finally {
      setIsCreatingEntity(false)
    }
  }

  // Handle refine request
  const handleRefine = async (suggestion: { type: string; label: string }) => {
    await sendMessage(
      `Can you refine the ${suggestion.type} suggestion "${suggestion.label}"? I'd like more detail or a different angle.`,
      {
        mode: 'flow',
        phaseId: phase?.id,
        phaseName: phase?.name,
        journeyMode: 'structured',
        flowContext: buildFlowContext(),
      }
    )
  }

  // Calculate progress percentage
  const calculateProgress = useCallback(() => {
    const requiredCards = entityCards.filter(c => c.isRequired)
    if (requiredCards.length === 0) return 100

    const filledCards = requiredCards.filter(c => c.currentCount >= c.minCount)
    return Math.round((filledCards.length / requiredCards.length) * 100)
  }, [entityCards])

  // Handle quick action click
  const handleQuickActionClick = useCallback(async (action: QuickAction) => {
    let message = ''
    switch (action.type) {
      case 'generate':
        message = action.entityType
          ? `Generate a ${action.entityType} for me`
          : 'Generate an entity for me'
        break
      case 'validate':
        message = action.targetLabel
          ? `Validate "${action.targetLabel}" - is it specific and actionable enough?`
          : 'Validate this entity'
        break
      case 'examples':
        message = action.entityType
          ? `Show me examples of ${action.entityType}`
          : 'Show me some examples'
        break
      case 'skip':
        message = 'I want to skip this and move forward'
        break
    }

    await sendMessage(message, {
      mode: 'flow',
      phaseId: phase?.id,
      phaseName: phase?.name,
      journeyMode: 'structured',
      flowContext: buildFlowContext(),
    })
  }, [sendMessage, phase, buildFlowContext])

  // Handle entity mention click
  const handleEntityMentionClick = useCallback((_entityId: string) => {
    // TODO: Navigate to entity or show entity details
  }, [])

  // Not configured message
  if (!apiKey) {
    return (
      <div className={cn('flex flex-col bg-white h-full', className)}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-warm-100 flex items-center justify-center mb-3">
            <AlertCircle className="w-6 h-6 text-warm-500" />
          </div>
          <h3 className="text-base font-semibold text-warm-700 mb-1">AI Not Configured</h3>
          <p className="text-sm text-text-muted">
            Add your Claude API key in Settings.
          </p>
        </div>
      </div>
    )
  }

  // No phase message
  if (!phase) {
    return (
      <div className={cn('flex flex-col bg-white h-full', className)}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-business/10 flex items-center justify-center mb-3">
            <Layout className="w-6 h-6 text-business" />
          </div>
          <h3 className="text-base font-semibold text-warm-700 mb-1">Select a Phase</h3>
          <p className="text-sm text-text-muted">
            Start your journey to get AI assistance.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col bg-white h-full', className)}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-warm-200 bg-gradient-to-r from-warm-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-business to-market flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-warm-800">Flow Coach</h2>
              <p className="text-xs text-text-muted">{phase.name}</p>
            </div>
          </div>

          {/* Guided Mode Toggle */}
          <Button
            variant={assistantMode === 'guided' ? 'default' : 'outline'}
            size="sm"
            onClick={handleToggleGuided}
            className={cn(
              'h-7 px-2 text-xs gap-1 transition-colors',
              assistantMode === 'guided'
                ? 'bg-business hover:bg-business/90 text-white'
                : 'bg-white hover:bg-warm-50 text-warm-600 border-warm-300'
            )}
            title={assistantMode === 'guided' ? 'Guided mode: AI proactively helps' : 'Switch to guided mode'}
          >
            {assistantMode === 'guided' ? (
              <>
                <ToggleRight className="h-3 w-3" />
                <span className="font-medium">Guided</span>
              </>
            ) : (
              <>
                <ToggleLeft className="h-3 w-3" />
                <span className="font-medium">Standard</span>
              </>
            )}
          </Button>
        </div>

        {/* Guided Mode Indicator */}
        {assistantMode === 'guided' && (
          <div className="mt-2 px-3 py-1.5 bg-business/10 border border-business/20 rounded-md">
            <p className="text-xs text-business font-medium">
              Guided Mode: AI proactively helps fill entities
            </p>
          </div>
        )}
      </div>

      {/* Phase Progress Section */}
      {assistantMode === 'guided' && (
        <PhaseProgressSection
          phase={phase}
          progress={calculateProgress()}
          entityCards={entityCards}
          nextPhase={undefined} // TODO: Get next phase from phases array
          onEntityClick={(_entityType) => {
            // TODO: Handle entity type click - navigate to create dialog
          }}
        />
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        {/* Error Banner */}
        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-red-700">Error</p>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Welcome message if no messages */}
        {messages.length === 0 && !isLoading && !error && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-12 h-12 rounded-full bg-business/10 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-business" />
            </div>
            <h3 className="text-sm font-semibold text-warm-700 mb-1">
              {assistantMode === 'guided'
                ? "Let's complete this phase together!"
                : 'Ask me anything'}
            </h3>
            <p className="text-xs text-text-muted leading-relaxed">
              {assistantMode === 'guided'
                ? 'I\'ll help you create the entities you need step by step.'
                : 'Ask about your product discovery journey.'}
            </p>
          </div>
        )}

        {/* Message list */}
        {messages.map((message) => (
          <div key={message.id}>
            <MessageBubble
              message={message}
              onEntityMentionClick={handleEntityMentionClick}
            />
            {/* Entity suggestion card */}
            {message.role === 'assistant' && message.suggestedEntity && (
              <div className="mt-2 ml-8">
                <EntitySuggestionCard
                  suggestion={message.suggestedEntity}
                  onAccept={() =>
                    handleAcceptSuggestion(
                      message.suggestedEntity!.type,
                      message.suggestedEntity!.label,
                      message.suggestedEntity!.description
                    )
                  }
                  onRefine={() => handleRefine(message.suggestedEntity!)}
                  isCreating={isCreatingEntity}
                />
              </div>
            )}
            {/* Quick actions bar */}
            {message.role === 'assistant' && message.quickActions && message.quickActions.length > 0 && (
              <div className="mt-2 ml-8">
                <QuickActionsBar
                  actions={message.quickActions}
                  onActionClick={handleQuickActionClick}
                  disabled={isLoading || isStreaming}
                />
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-3 border-t border-warm-200 bg-warm-50/50">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isStreaming
                ? 'Thinking...'
                : assistantMode === 'guided'
                  ? 'Tell me about your product...'
                  : 'Ask a question...'
            }
            rows={2}
            className="w-full resize-none rounded-lg border border-warm-200 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-business/30 focus:border-business/30 placeholder:text-warm-400 bg-white"
            disabled={isLoading || isStreaming}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading || isStreaming}
            className="absolute right-1.5 bottom-1.5 h-7 w-7 p-0 rounded-md bg-business hover:bg-business/90"
          >
            {isLoading || isStreaming ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
        <div className="mt-1.5 text-[10px] text-warm-400">
          Enter to send • Shift+Enter for new line
        </div>
      </div>
    </div>
  )
}

// Message Bubble Component
function MessageBubble({
  message,
  onEntityMentionClick,
}: {
  message: { role: string; content: string; isStreaming?: boolean }
  onEntityMentionClick?: (entityId: string) => void
}) {
  const isUser = message.role === 'user'

  // Parse entity mentions for assistant messages
  const parsedContent = !isUser && message.content ? parseEntityMentions(message.content) : [message.content]

  return (
    <div className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar - only for assistant */}
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-business to-market flex items-center justify-center flex-shrink-0">
          <Layout className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Message content */}
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3 py-2',
          isUser && 'bg-warm-700 text-white rounded-br-sm',
          !isUser && 'bg-warm-100 text-warm-800 rounded-bl-sm'
        )}
      >
        <div
          className={cn(
            'text-sm leading-relaxed whitespace-pre-wrap',
            message.isStreaming && 'animate-pulse'
          )}
        >
          {isUser ? (
            <>
              {message.content || (message.isStreaming ? '...' : '')}
              {message.isStreaming && message.content && (
                <span className="inline-block w-1 h-3 bg-business ml-0.5 animate-pulse rounded-sm" />
              )}
            </>
          ) : (
            <>
              {parsedContent.map((part, index) =>
                typeof part === 'string' ? (
                  <span key={index}>{part}</span>
                ) : (
                  <EntityMentionChip
                    key={index}
                    entityId={part.entityId}
                    label={part.label}
                    domain="market" // TODO: Get actual domain from entity
                    onClick={() => onEntityMentionClick?.(part.entityId)}
                  />
                )
              )}
              {message.isStreaming && message.content && (
                <span className="inline-block w-1 h-3 bg-business ml-0.5 animate-pulse rounded-sm" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
